import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatShortDate } from "@/lib/format";

/**
 * 7-day forecast rendered as a horizontal strip of day columns
 * (NOT a table). Each day shows:
 *   day-of-week
 *   date
 *   occupancy % + subtle bar
 *   available, arrivals, departures, ADR
 * TODAY column is highlighted with soft Akomoda orange.
 */
export default function Forecast7Days({ days, currency }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    if (!days || days.length === 0) return null;

    const dayLabels = t("dashboard.day_labels_short").split("|");
    const localizedDay = (dstr, idx) => {
        if (idx === 0) return dayLabels[0];
        if (idx === 1) return dayLabels[1];
        const dow = new Date(dstr).getDay();
        const map = [dayLabels[8], dayLabels[2], dayLabels[3], dayLabels[4], dayLabels[5], dayLabels[6], dayLabels[7]];
        return map[dow] || "";
    };

    const barColor = (pct) => {
        if (pct >= 95) return "bg-rose-500";
        if (pct >= 90) return "bg-orange-500";
        if (pct >= 80) return "bg-[#ff7b02]";
        return "bg-slate-400";
    };

    const metricRows = [
        { key: "available",  label: t("dashboard.available_rooms"), render: (d) => d.available },
        { key: "arrivals",   label: t("dashboard.arrivals"),        render: (d) => d.arrivals },
        { key: "departures", label: t("dashboard.departures"),      render: (d) => d.departures },
        { key: "adr",        label: t("dashboard.adr_short"),       render: (d) => d.adr ? formatCurrency(d.adr, currency) : "—" },
    ];

    return (
        <section className="rounded-[10px] border border-border bg-card" data-testid="forecast-strip">
            <div className="flex items-center justify-between px-5 py-3">
                <h2 className="text-[15px] font-semibold text-foreground">
                    {t("dashboard.next_7_days")}
                </h2>
            </div>

            <div className="overflow-x-auto">
                <div className="grid min-w-[720px] grid-cols-7">
                    {days.map((d, i) => {
                        const isToday = i === 0;
                        return (
                            <button
                                key={d.date}
                                onClick={() => navigate(`/reservations?date=${d.date}`)}
                                data-testid={`forecast-day-${d.date}`}
                                className={`group relative flex flex-col items-stretch gap-2 border-l border-border/60 px-3 py-4 text-left transition first:border-l-0 hover:bg-muted/60 ${
                                    isToday ? "bg-[#ff7b02]/[0.04]" : ""
                                }`}
                            >
                                {/* Day header */}
                                <div>
                                    <div className={`text-[11px] font-semibold uppercase tracking-wider ${isToday ? "text-[#ff7b02]" : "text-muted-foreground"}`}>
                                        {localizedDay(d.date, i)}
                                    </div>
                                    <div className="mt-0.5 font-mono text-[11px] text-muted-foreground/70">
                                        {formatShortDate(d.date)}
                                    </div>
                                </div>

                                {/* Occupancy — the visual star */}
                                <div>
                                    <div className={`text-[22px] font-semibold leading-none tracking-tight ${
                                        d.occ_pct >= 95 ? "text-rose-600" :
                                        d.occ_pct >= 90 ? "text-orange-600" :
                                        "text-foreground"
                                    }`}>
                                        {d.occ_pct}%
                                    </div>
                                    <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-muted">
                                        <div
                                            className={`h-full ${barColor(d.occ_pct)} transition-all`}
                                            style={{ width: `${Math.min(100, d.occ_pct)}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Metrics */}
                                <dl className="mt-1 space-y-1">
                                    {metricRows.map((m) => (
                                        <div key={m.key} className="flex items-center justify-between text-[11.5px]">
                                            <dt className="text-muted-foreground">{m.label}</dt>
                                            <dd className="tabular-nums text-foreground">{m.render(d)}</dd>
                                        </div>
                                    ))}
                                </dl>

                                {d.occ_pct >= 90 && (
                                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-orange-600">
                                        {t("dashboard.high_occupancy")}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
